import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, RotateCcw } from "lucide-react";
import { propertyTypes, helpTypes, recommend } from "@/data/services";

export default function IntakeSelector() {
  const [prop, setProp] = React.useState(null);
  const [help, setHelp] = React.useState(null);

  const result = prop && help ? recommend(prop, help) : null;

  const reset = () => { setProp(null); setHelp(null); };

  return (
    <section className="bg-white text-brand-black" data-testid="intake-selector">
      <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
          <div className="lg:col-span-7">
            <div className="eyebrow-accent">Build a starting plan</div>
            <h2 className="mt-4 text-[32px] md:text-5xl font-bold tracking-tight leading-[1.06]">
              Tell us the property. Tell us the help. We'll suggest a starting point.
            </h2>
          </div>
          <p className="lg:col-span-5 text-[15px] leading-relaxed text-brand-gray max-w-lg">
            Two quick choices. LotWise Home returns a suggested plan, the services likely involved,
            and the next step. Final pricing is confirmed by quote after we review the property.
          </p>
        </div>

        <div className="border border-brand-gray/25 bg-white p-6 md:p-8">
          {/* Step 1 */}
          <div>
            <div className="flex items-center gap-3">
              <span className="text-[11px] tracking-[0.2em] uppercase text-brand-gradient font-semibold">Step 01</span>
              <div className="h-px flex-1 bg-brand-gray/20" />
              {prop && <button type="button" onClick={reset} className="text-[11px] tracking-[0.14em] uppercase text-brand-gray hover:text-brand-black inline-flex items-center gap-1" data-testid="intake-reset"><RotateCcw className="h-3 w-3"/> Reset</button>}
            </div>
            <h3 className="mt-3 text-xl md:text-2xl font-bold tracking-tight">What are you preparing?</h3>
            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {propertyTypes.map((p) => {
                const active = prop === p.value;
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setProp(p.value)}
                    data-testid={`intake-property-${p.value}`}
                    className={`text-left border p-4 transition-colors ${active ? "border-brand-black bg-brand-black text-white" : "border-brand-gray/25 hover:border-brand-black"}`}
                  >
                    <div className={`text-[10px] tracking-[0.22em] uppercase ${active ? "text-white/70" : "text-brand-gray"}`}>Type</div>
                    <div className={`mt-2 text-[14.5px] font-semibold ${active ? "text-white" : "text-brand-black"}`}>{p.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2 */}
          {prop && (
            <div className="mt-10 pt-8 border-t border-brand-gray/20">
              <div className="flex items-center gap-3">
                <span className="text-[11px] tracking-[0.2em] uppercase text-brand-gradient font-semibold">Step 02</span>
                <div className="h-px flex-1 bg-brand-gray/20" />
              </div>
              <h3 className="mt-3 text-xl md:text-2xl font-bold tracking-tight">What do you need help with?</h3>
              <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {helpTypes.map((h) => {
                  const active = help === h.value;
                  return (
                    <button
                      key={h.value}
                      type="button"
                      onClick={() => setHelp(h.value)}
                      data-testid={`intake-help-${h.value}`}
                      className={`text-left border p-4 transition-colors ${active ? "border-brand-black bg-brand-black text-white" : "border-brand-gray/25 hover:border-brand-black"}`}
                    >
                      <div className={`text-[10px] tracking-[0.22em] uppercase ${active ? "text-white/70" : "text-brand-gray"}`}>Help</div>
                      <div className={`mt-2 text-[14.5px] font-semibold ${active ? "text-white" : "text-brand-black"}`}>{h.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="mt-10 pt-8 border-t border-brand-gray/20 grid lg:grid-cols-12 gap-6" data-testid="intake-result">
              <div className="lg:col-span-4">
                <div className="eyebrow-accent">Suggested starting point</div>
                <h3 className="mt-3 text-2xl md:text-3xl font-bold tracking-tight">{result.planLabel}</h3>
                <p className="mt-3 text-[13.5px] leading-relaxed text-brand-gray">
                  Based on your inputs, this is where we would start the conversation. Final scope and
                  quote are confirmed after we review the property, location, timeline, and deliverables.
                </p>
              </div>
              <div className="lg:col-span-5">
                <div className="eyebrow text-brand-gray">Services likely involved</div>
                <ul className="mt-4 space-y-2">
                  {result.services.map((s) => (
                    <li key={s} className="flex items-start gap-2.5 text-[14px]">
                      <span className="mt-1 shrink-0 h-2 w-2 rounded-full bg-gradient-to-br from-brand-blue to-brand-violet" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-3 flex flex-col justify-between">
                <div>
                  <div className="eyebrow text-brand-gray">Next step</div>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-brand-gray">
                    Send us the property details. We'll confirm scope and follow up with a plan.
                  </p>
                </div>
                <Link
                  to={`/contact?plan=${result.plan}&property=${prop}&help=${help}`}
                  data-testid="intake-cta"
                  className="mt-4 group inline-flex items-center justify-between gap-2 bg-brand-black text-white px-5 py-4 text-[13px] font-bold tracking-tight hover:bg-brand-violet transition-colors"
                >
                  {result.next}
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
